// Platform Initialization - Analytics React component for initializing all platforms
// Extracted from Analytics.js for better separation of concerns

import * as React from 'react';
import mixpanel from 'mixpanel-browser';
import Clarity from '@microsoft/clarity';
import { debugLog } from '../../utils/debugUtils';
import {
  isPreviewOrInternalDomain,
  shouldInitializeAnalytics,
} from '../../utils/environmentUtils';
import {
  logCriticalAnalyticsError,
  trackAnalyticsErrorPattern,
} from '../../utils/debugUtils';
import { setMixpanelReady } from './queueManagement';
import { initializeDebugUtils, updateDebugQueueStatus } from './debugUtils';

/**
 * Pure analytics initialization functions (environment-agnostic for testing)
 */

/**
 * Initialize Google Analytics and check availability
 */
export const initializeGoogleAnalytics = () => {
  try {
    if (window.gtag) {
      debugLog('✅ Google Analytics (gtag) detected and ready');
      trackAnalyticsErrorPattern('google_analytics_init', true);
    } else {
      debugLog('⚠️ Google Analytics (gtag) not detected');
      trackAnalyticsErrorPattern('google_analytics_init', false);

      // Send Google Analytics unavailability to Netlify function for monitoring
      logCriticalAnalyticsError(
        'google_analytics_not_detected',
        'Google Analytics (gtag) not available during initialization',
        {
          userAgent:
            typeof navigator !== 'undefined' ? navigator.userAgent : 'unknown',
          timestamp: new Date().toISOString(),
          url: typeof window !== 'undefined' ? window.location.href : 'unknown',
          gtagDefined: typeof window.gtag !== 'undefined',
          windowGtag: !!window.gtag,
        },
        'warning'
      );
    }
  } catch (error) {
    debugLog('❌ Error checking Google Analytics availability:', error);
    trackAnalyticsErrorPattern('google_analytics_init', false);

    // Send Google Analytics check failure to Netlify function for monitoring
    logCriticalAnalyticsError(
      'google_analytics_check_failed',
      `Error checking Google Analytics availability: ${error.message}`,
      {
        error: error.message,
        stack: error.stack,
        userAgent:
          typeof navigator !== 'undefined' ? navigator.userAgent : 'unknown',
        url: typeof window !== 'undefined' ? window.location.href : 'unknown',
      },
      'error'
    );
  }
};

/**
 * Initialize Mixpanel with configuration and timezone setup
 */
export const initializeMixpanel = (options = {}) => {
  const {
    token = process.env.GATSBY_MIXPANEL_TOKEN,
    skipEnvironmentCheck = false,
  } = options;

  // Allow tests to skip environment check
  if (!skipEnvironmentCheck && !shouldInitializeAnalytics()) {
    debugLog('🚫 Mixpanel disabled - not on main production domain');
    return;
  }

  // Check if token is present
  if (!token) {
    debugLog('⚠️ Mixpanel token not configured, skipping initialization');
    return;
  }

  try {
    // Initialize Mixpanel with environment token
    mixpanel.init(token, {
      debug: process.env.NODE_ENV !== 'production',
      track_pageview: 'url-with-path', // SPA tracking - tracks when path changes (separate from Meta tracking)
      persistence: 'localStorage',
      record_sessions_percent: 100, // Record 100% of all sessions
      record_mask_text_selector: '', //unmask all text elements
      record_block_selector: '', //unmask images and videos
      ip: true,
      property_blacklist: [],
      opt_out_tracking_by_default: false,
      secure_cookie: true,
    });

    // Mark Mixpanel as ready and process queued events
    setMixpanelReady(true);
    debugLog('✅ Mixpanel initialized successfully');

    // Update debug functions now that Mixpanel is ready
    updateDebugQueueStatus();

    // Add timezone context to help reduce "(not set)" users
    setupMixpanelTimezoneContext();
  } catch (error) {
    debugLog('❌ Mixpanel initialization failed:', error);
    setMixpanelReady(false);

    // Log critical error for monitoring
    logCriticalAnalyticsError(
      'mixpanel_init_failed',
      `Mixpanel initialization failed: ${error.message}`,
      {
        error: error.message,
        stack: error.stack,
        userAgent:
          typeof navigator !== 'undefined' ? navigator.userAgent : 'unknown',
      },
      'critical'
    );
  }
};

/**
 * Setup Mixpanel timezone context
 */
const setupMixpanelTimezoneContext = () => {
  // Reduced timeout to minimize the window where events lack timezone context
  setTimeout(() => {
    try {
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      if (timezone) {
        const timezoneData = {
          user_timezone: timezone,
          browser_language: navigator.language || 'unknown',
          timezone_offset_minutes: new Date().getTimezoneOffset(),
        };

        // Register for all future events and set on user profile
        try {
          mixpanel.register(timezoneData);
          mixpanel.people.set(timezoneData);

          debugLog('✅ Timezone context added:', timezoneData);

          // Track the enhancement attempt directly in Mixpanel (avoid circular dependency)
          mixpanel.track('location_enhancement_attempt', {
            timezone_added: true,
            timezone: timezone,
            note: 'Timezone context added to supplement Mixpanel IP geolocation',
          });
        } catch (mixpanelError) {
          debugLog(
            'Failed to register timezone data with Mixpanel:',
            mixpanelError
          );
        }
      }
    } catch (error) {
      debugLog('Failed to get timezone:', error);

      // Track failed attempt directly in Mixpanel (avoid circular dependency)
      if (typeof mixpanel !== 'undefined' && mixpanel.track) {
        try {
          mixpanel.track('location_enhancement_attempt', {
            timezone_added: false,
            error: error.message,
            note: 'Failed to add timezone context',
          });
        } catch (trackError) {
          debugLog('Failed to track timezone failure:', trackError);
        }
      }
    }
  }, 500); // Reduced from 1000ms to 500ms for faster setup
};

/**
 * Initialize Clarity with error handling
 */
export const initializeClarity = (options = {}) => {
  const {
    projectId = process.env.GATSBY_CLARITY_PROJECT_ID,
    skipEnvironmentCheck = false,
  } = options;

  // Allow tests to skip environment check
  if (!skipEnvironmentCheck && !shouldInitializeAnalytics()) {
    debugLog('🚫 Clarity disabled - not on main production domain');
    return;
  }

  if (!projectId) {
    debugLog('⚠️ Clarity project ID not configured, skipping initialization');
    return;
  }

  try {
    if (
      typeof Clarity !== 'undefined' &&
      Clarity &&
      typeof Clarity.init === 'function'
    ) {
      Clarity.init(projectId);
      debugLog('✅ Clarity initialized successfully');
      trackAnalyticsErrorPattern('clarity_init', true);
    } else {
      debugLog('⚠️ Clarity not available during initialization');
      trackAnalyticsErrorPattern('clarity_init', false);

      // Send Clarity unavailability to Netlify function for monitoring
      logCriticalAnalyticsError(
        'clarity_not_available',
        'Clarity not available during initialization',
        {
          clarityDefined: typeof Clarity !== 'undefined',
          clarityObject: !!Clarity,
          hasInitMethod: Clarity && typeof Clarity.init === 'function',
          userAgent:
            typeof navigator !== 'undefined' ? navigator.userAgent : 'unknown',
          url: typeof window !== 'undefined' ? window.location.href : 'unknown',
          timestamp: new Date().toISOString(),
        },
        'warning'
      );
    }
  } catch (error) {
    debugLog('❌ Clarity initialization failed:', error);
    trackAnalyticsErrorPattern('clarity_init', false);

    // Send Clarity initialization failure to Netlify function for monitoring
    logCriticalAnalyticsError(
      'clarity_init_failed',
      `Clarity initialization failed: ${error.message}`,
      {
        error: error.message,
        stack: error.stack,
        clarityAvailable: typeof Clarity !== 'undefined',
        clarityObject: !!Clarity,
        userAgent:
          typeof navigator !== 'undefined' ? navigator.userAgent : 'unknown',
        url: typeof window !== 'undefined' ? window.location.href : 'unknown',
      },
      'warning' // Lower severity since Clarity is less critical
    );
  }
};

/**
 * Core analytics initialization logic (pure function for testing)
 */
export const initializeAllAnalytics = (options = {}) => {
  const {
    skipEnvironmentCheck = false,
    mixpanelToken,
    clarityProjectId,
  } = options;

  // Initialize debug functions
  initializeDebugUtils();

  // Initialize all platforms
  initializeGoogleAnalytics();
  initializeMixpanel({
    token: mixpanelToken,
    skipEnvironmentCheck,
  });
  initializeClarity({
    projectId: clarityProjectId,
    skipEnvironmentCheck,
  });
};

/**
 * Production Analytics component with environment checks
 * Pure production component - no test concerns
 */
const Analytics = () => {
  React.useEffect(() => {
    // Environment checks
    if (isPreviewOrInternalDomain()) {
      debugLog('Analytics disabled for non-production environment');
      return;
    }

    if (!shouldInitializeAnalytics()) {
      debugLog('Analytics disabled - not on main production domain');
      return;
    }

    // Initialize with environment checks
    initializeAllAnalytics();
  }, []);

  return null;
};

export default Analytics;
